// Live re-pairing of recipe ingredients against the current pantry.
//
// The universal rule: pairings are NEVER persisted on the recipe —
// a saved recipe stores canonical refs + dietary intent only. When
// the user opens a preview or enters CookMode, we re-run this
// pairing pass so "We'll use your Great Value Chicken Breast"
// reflects TODAY's pantry, not whatever was stocked the week the
// recipe was first drafted. A month later, if the user has
// different brands or inventory, the pairing is correct without
// any schema rot.
//
// Primitives lifted out of AIRecipe so both the AI-Recipe tweak
// surface, the preview screen, and CookMode can share one
// implementation. Import what you need; everything is pure.

import {
  findIngredient,
  resolveSlug,
  resolveCanonicalIdentity,
  INGREDIENTS,
  fuzzyMatchIngredient,
  ALL_STATE_TOKENS,
  cutLabel,
  sharesDisqualifyingModifier,
  getIngredientInfo,
  inferCanonicalFromName,
} from "../data/ingredients";
import { buildDisplayNameParts } from "./pantryHelpers";

// ── curated substitution tiers ───────────────────────────────────────
// Authored on INGREDIENT_INFO[id].substitutions. Lower number = higher
// confidence the swap reads like the original. Used to rank when
// multiple authored subs are simultaneously in the pantry (recipe
// wants parmesan, user has both pecorino + grana_padano stocked —
// pick the higher-priority one to feature).
const SUB_TIER_RANK = { direct: 1, pro: 2, emergency: 3, dietary: 4 };
const SUB_TIER_LABEL = {
  direct:    "Direct sub",
  pro:       "Upgrade",
  emergency: "In a pinch",
  dietary:   "Dietary swap",
};
export function subTierLabel(tier) {
  return tier && SUB_TIER_LABEL[tier] ? SUB_TIER_LABEL[tier] : null;
}

// ── dietary modifiers ────────────────────────────────────────────────
// "low-carb tortilla" is a tortilla with a claim tag, not a different
// ingredient. We strip these for matching but preserve them as claim
// labels the recipe remembers. If the paired pantry row doesn't
// carry the same claim, pair() surfaces it as `lostClaims` so the
// UI can warn "NO LONGER KETO".
const DIET_MODIFIERS = [
  { re: /\blow[\s\-]?carb\b/gi,        label: "Low-Carb" },
  { re: /\blow[\s\-]?fat\b/gi,         label: "Low-Fat" },
  { re: /\blow[\s\-]?sugar\b/gi,       label: "Low-Sugar" },
  { re: /\blow[\s\-]?sodium\b/gi,      label: "Low-Sodium" },
  { re: /\bzero[\s\-]?carb\b/gi,       label: "Zero-Carb" },
  { re: /\b(?:zero|no)[\s\-]?sugar\b/gi, label: "Sugar-Free" },
  { re: /\bsugar[\s\-]?free\b/gi,      label: "Sugar-Free" },
  { re: /\bgluten[\s\-]?free\b/gi,     label: "Gluten-Free" },
  { re: /\bdairy[\s\-]?free\b/gi,      label: "Dairy-Free" },
  { re: /\bgrain[\s\-]?free\b/gi,      label: "Grain-Free" },
  { re: /\bfat[\s\-]?free\b/gi,        label: "Fat-Free" },
  { re: /\bhigh[\s\-]?protein\b/gi,    label: "High-Protein" },
  { re: /\bwhole[\s\-]?wheat\b/gi,     label: "Whole-Wheat" },
  { re: /\bwhole[\s\-]?grain\b/gi,     label: "Whole-Grain" },
  { re: /\bmulti[\s\-]?grain\b/gi,     label: "Multigrain" },
  { re: /\bketo\b/gi,                  label: "Keto" },
  { re: /\bpaleo\b/gi,                 label: "Paleo" },
  { re: /\bwhole30\b/gi,               label: "Whole30" },
  { re: /\bvegan\b/gi,                 label: "Vegan" },
  { re: /\bvegetarian\b/gi,            label: "Vegetarian" },
  { re: /\borganic\b/gi,               label: "Organic" },
];

export function extractDietaryClaims(name) {
  if (!name) return { claims: [], stripped: "" };
  let stripped = String(name);
  const found = new Set();
  for (const { re, label } of DIET_MODIFIERS) {
    re.lastIndex = 0;
    if (re.test(stripped)) {
      found.add(label);
      re.lastIndex = 0;
      stripped = stripped.replace(re, " ");
    }
  }
  return {
    claims: [...found],
    stripped: stripped.replace(/\s+/g, " ").trim(),
  };
}

// ── name normalization ───────────────────────────────────────────────
// State terms are a separate identity axis per CLAUDE.md (SET STATE
// row, purple). "chicken breast, cubed" and "chicken breast" are the
// same canonical ingredient — only the prep differs. Strip state
// tokens in ANY position (prefix "sliced chicken", comma-suffix
// "chicken breast, cubed", paren-suffix "chicken breast (cubed)",
// inline "ground fresh pork") so the head noun survives for
// identity matching.
//
// Token list is DERIVED from the registry's INGREDIENT_STATES +
// STATE_LABELS via ALL_STATE_TOKENS, so adding a new state in one
// place propagates here automatically — no parallel hardcoded list.
// Regex is built once at module-init by escaping and joining every
// known state token. Word-boundary anchoring catches any position.
const STATE_TOKEN_RE = (() => {
  const escaped = [...ALL_STATE_TOKENS]
    .filter(t => t && /^[a-z]+$/i.test(t))
    .sort((a, b) => b.length - a.length)
    .join("|");
  return escaped ? new RegExp(`\\b(?:${escaped})\\b`, "gi") : /(?!)/;
})();
const BRAND_TOKEN_RE = /\b(gv|great\s*value|kroger|kro|organic|simple\s*truth|365|trader\s*joe'?s?|tj)\b/gi;
const SIZE_TOKEN_RE  = /\b\d+(\.\d+)?\s*(oz|lb|lbs|g|kg|ml|l|ct|count|pack|pk|bag|jar|can|box|tub)\b/gi;

export function normalizeForMatch(name) {
  if (!name) return "";
  const { stripped } = extractDietaryClaims(name);
  return String(stripped)
    .toLowerCase()
    .replace(BRAND_TOKEN_RE, " ")
    .replace(SIZE_TOKEN_RE, " ")
    .replace(STATE_TOKEN_RE, " ")
    .replace(/[^a-z ]+/g, " ")
    .replace(/s\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Token-level match. Naked `.includes()` was too loose: "tortilla"
// substring-hits "tortilla chip" and "spice mix" overlaps with
// "mexican spice mix", so the AI-sketch auto-pairer was happily
// swapping four tortillas for tortilla chips and a Mexican spice
// blend for a jar of jalapeños. English food names carry identity
// in the HEAD NOUN (the last token) — "corn tortilla", "flour
// tortilla", "whole wheat tortilla" all share head=tortilla and
// are legitimate subs; "tortilla chip" has head=chip, a different
// ingredient entirely. We require (a) heads match AND (b) the
// shorter side's token set is fully contained in the longer's AND
// (c) no disqualifying modifier (see HEAD_NOUN_MODIFIER_EXCLUSIONS
// — blocks peanut-butter-as-butter, coconut-milk-as-milk, etc.).
export function namesMatch(a, b) {
  const na = normalizeForMatch(a);
  const nb = normalizeForMatch(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const ta = na.split(/\s+/).filter(Boolean);
  const tb = nb.split(/\s+/).filter(Boolean);
  if (!ta.length || !tb.length) return false;
  const head = ta[ta.length - 1];
  if (head !== tb[tb.length - 1]) return false;
  const sa = new Set(ta);
  const sb = new Set(tb);
  const [small, big] = sa.size <= sb.size ? [sa, sb] : [sb, sa];
  for (const t of small) if (!big.has(t)) return false;
  if (sharesDisqualifyingModifier(small, big, head)) return false;
  return true;
}

// True when two canonical slugs belong to the same ingredient family —
// either the exact same slug, or both children of the same _hub parent.
// "chicken" and "chicken_breast" both hang off chicken_hub, so a recipe
// asking for generic Chicken should pair with the user's Chicken
// Breast even though the leaf slugs differ. Hub-level equivalence is
// the identity-stable way to match across cuts without falling back
// to fragile name-string tricks (which tightened namesMatch no longer
// allows — "chicken" and "chicken breast" have different head nouns).
//
// Use sameCanonicalFamily for PAIRING questions ("does the user have
// something that can cover this recipe slot?"). Do NOT use it for
// DEDUCTION questions ("which pantry rows come out when the user
// cooks this?") — hub siblings like heavy_cream + milk are the same
// family but different canonicals, and you don't drain milk to cook
// with heavy cream. resolvesToSameCanonical below is the helper for
// the deduction path.
export function sameCanonicalFamily(slugA, slugB) {
  if (!slugA || !slugB) return false;
  if (slugA === slugB) return true;
  const a = findIngredient(slugA);
  const b = findIngredient(slugB);
  const pa = a?.parentId || a?.id || null;
  const pb = b?.parentId || b?.id || null;
  if (!pa || !pb) return false;
  return pa === pb;
}

// Narrow companion to sameCanonicalFamily: true iff two slugs resolve
// to the SAME canonical after alias redirect. Catches the legacy-
// compound-slug case (chicken_breast alias → chicken matches a
// pantry row tagged chicken + cut=breast) without widening to hub
// siblings. Use this for deduction paths where spending one
// ingredient on another's row would be wrong (heavy_cream recipe
// must NOT drain milk rows, even though they share milk_hub).
export function resolvesToSameCanonical(slugA, slugB) {
  if (!slugA || !slugB) return false;
  if (slugA === slugB) return true;
  const a = resolveCanonicalIdentity(slugA);
  const b = resolveCanonicalIdentity(slugB);
  return !!a.canonical && !!b.canonical && a.canonical === b.canonical;
}

// Resolve a free-text name to a canonical slug via best-score fuzzy
// match. Score-based rather than first-match-wins so "flour
// tortilla" doesn't land on `flour` (listed before `tortillas`).
export function resolveNameToCanonicalId(name) {
  if (!name) return null;
  const { stripped } = extractDietaryClaims(name);
  const needle = (stripped || name).trim();
  if (!needle) return null;
  const hits = fuzzyMatchIngredient(needle, 1);
  const top = Array.isArray(hits) && hits.length > 0 ? hits[0] : null;
  if (top && top.ingredient && typeof top.score === "number" && top.score >= 60) {
    return top.ingredient.id;
  }
  return null;
}

// ── row identity ─────────────────────────────────────────────────────
// HEADER per CLAUDE.md: [Brand] [State] [Canonical] — the big italic
// title, composed via the shared buildDisplayNameParts so pairing
// surfaces, kitchen cards, and scan rows all read the same identity
// (never a hand-rolled `${brand} ${name}`; sentinel pseudo-brands are
// skipped centrally). Two segments stay OUT of this header on
// purpose:
//   • CUT renders as its own chip via deriveRowCut on every
//     consuming surface — including it here would double it.
//   • Package size is a storage concern; this header feeds pairing
//     rows and swap prose ("Using Kerrygold Butter instead of
//     butter"), where "(16 oz)" would read as noise.
//
// Legacy compound slugs (chicken_breast, ribeye, ...) still resolve —
// findIngredient inside the parts builder walks CANONICAL_ALIASES as
// read-compat, and the cut hint surfaces via deriveRowCut.
export function deriveRowHeader(row) {
  if (!row) return "";
  const canonId = row.ingredientId || row.canonicalId || null;
  const parts = buildDisplayNameParts({ ...row, canonicalId: canonId })
    .filter(p => p.kind === "brand" || p.kind === "state" || p.kind === "canonical");
  if (parts.length > 0) return parts.map(p => p.text).join(" ");
  return row.name || "";
}

// The CUT axis row. Sources in priority order:
//   1. row.cut — new explicit column (migration 0122), authoritative
//   2. alias.cut — legacy compound slug still in ingredient_id /
//                  canonical_id, decoded via resolveSlug
// Returns null when the row has no cut AND the canonical has no
// CUTS_FOR entry (produce, pantry staples — no cut axis rendering).
export function deriveRowCut(row) {
  if (!row) return null;
  if (row.cut) return cutLabel(row.cut);
  const canonId = row.ingredientId || row.canonicalId || null;
  const { cut } = resolveSlug(canonId);
  return cut ? cutLabel(cut) : null;
}

// ── claim diff ───────────────────────────────────────────────────────
// Collect everything claim-shaped off a pantry row: free-text claims
// the user or a scan attached, plus the label of any formal
// certification. Certifications count as claims for diet-loss
// purposes: USDA Organic IS Organic, Gluten-Free cert IS Gluten-Free.
function pantryRowClaimSet(row) {
  if (!row || !row.attributes) return new Set();
  const raw = [
    ...(Array.isArray(row.attributes.claims) ? row.attributes.claims : []),
    ...(Array.isArray(row.attributes.certifications)
      ? row.attributes.certifications.map(c => c?.label).filter(Boolean)
      : []),
  ];
  return new Set(raw.map(c => String(c).toLowerCase()));
}

function lostClaimsFor(requiredClaims, row) {
  if (!Array.isArray(requiredClaims) || requiredClaims.length === 0) return [];
  if (!row) return [];
  const have = pantryRowClaimSet(row);
  return requiredClaims.filter(c => !have.has(String(c).toLowerCase()));
}

// ── pairing ──────────────────────────────────────────────────────────
// Per-ingredient pairing against the current pantry.
//
// Output shape:
//   {
//     ingredient,            // the recipe row as-is
//     resolvedCanonicalId,   // canonical the recipe ROW resolves to,
//                            //   independent of whether a pantry row was
//                            //   found. Chain: ing.ingredientId →
//                            //   inferCanonicalFromName → fuzzy. Lets
//                            //   downstream surfaces (row badges, the
//                            //   nutrition resolver) read "what the
//                            //   recipe asked for" without re-running
//                            //   the inference cascade.
//     paired,                // canonical-match pantry row (state fit), or null
//     closestMatch,          // substitute pantry row, or null
//     subTier,               // "direct"|"pro"|"emergency"|"dietary"|null —
//                            //   from authored INGREDIENT_INFO substitutions,
//                            //   null when closestMatch came from generic
//                            //   hub/category fallback
//     subNote,               // human note from the authored sub (if any)
//     wrongStateCandidates,  // same-canonical pantry rows in the WRONG state
//                            //   (recipe asked for a state, user has the
//                            //   ingredient in a different form). Drives the
//                            //   WRONG FORM badge + "Make X from Y" hint.
//     lostClaims,            // recipe claims the chosen row doesn't carry
//     status,                // "paired" | "wrong-state" | "substitute" | "missing"
//   }
//
// "paired"       — same canonical, same state fit. Exact identity match.
// "wrong-state"  — same canonical exists in pantry but in a different
//                  physical form than the recipe asked for. Actionable —
//                  the user can convert (loaf → crumbs) or just accept it.
// "substitute"   — no canonical hit, but an authored substitution OR a
//                  same-hub / same-category sibling is on hand. Tier-aware:
//                  authored "direct" subs beat generic hub fallbacks.
// "missing"      — nothing close on hand. Shopping-list territory.
//
// Optional `options` argument:
//   ingredientInfo — { getInfo(canonicalId) → dbOverride } from
//                    useIngredientInfo(). Lets the pairer read DB-overlay
//                    substitutions for synthetic / admin-promoted slugs.
//                    Optional: bundled INGREDIENT_INFO is always read.
export function pairRecipeIngredients(ingredients, pantry, options = {}) {
  const ingredientInfo = options?.ingredientInfo || null;
  const used = new Set();
  const out = [];

  // Helper: fetch the highest-priority authored substitution for a
  // canonical that's actually in the pantry. Returns null when no
  // authored sub of that canonical is stocked. Walks both bundled
  // INGREDIENT_INFO and (when available) the DB overlay.
  const findCuratedSub = (ingCanonId) => {
    if (!ingCanonId) return null;
    const canon = findIngredient(ingCanonId);
    if (!canon) return null;
    const dbInfo = ingredientInfo?.getInfo ? ingredientInfo.getInfo(ingCanonId) : null;
    const info = getIngredientInfo(canon, dbInfo);
    const subs = Array.isArray(info?.substitutions) ? info.substitutions : [];
    if (subs.length === 0) return null;
    // Rank candidates by tier (direct > pro > emergency > dietary),
    // then pick the first that resolves to a real pantry row.
    const ranked = [...subs].sort((a, b) => {
      const ra = SUB_TIER_RANK[a?.tier || "direct"] || 99;
      const rb = SUB_TIER_RANK[b?.tier || "direct"] || 99;
      return ra - rb;
    });
    for (const sub of ranked) {
      if (!sub?.id) continue;
      const row = (pantry || []).find(p => {
        if (!rowIsRawIngredient(p)) return false;
        const pCanonId = p.ingredientId || p.canonicalId || null;
        if (!pCanonId) return false;
        return resolvesToSameCanonical(pCanonId, sub.id);
      });
      if (row) {
        return {
          row,
          tier: sub.tier || "direct",
          note: sub.note || null,
        };
      }
    }
    return null;
  };

  // Shared predicate across all match tiers: a row is a candidate
  // for standalone recipe-ingredient pairing ONLY when it's
  // kind="ingredient" (the raw pantry default). Compound dishes
  // and leftovers carry ingredients[] for composition tracking
  // but can't be cracked open to satisfy a standalone call.
  const rowIsRawIngredient = (p) => {
    if (!p || used.has(p.id)) return false;
    if ((p.kind || "ingredient") !== "ingredient") return false;
    // Defense-in-depth against the "leftover alfredo grabbed as butter"
    // bug: a prepared dish / leftover can reach here with kind dropped on
    // the JS boundary AND a stamped ingredient_id (a meal whose
    // ingredient_id got set to its first composition item, e.g. butter).
    // A raw ingredient is never sold/stored in SERVINGS and never carries a
    // multi-item composition — exclude anything that looks like a composed
    // meal so it can't satisfy a standalone "butter" call.
    if (p.unit === "serving") return false;
    const comp = p.ingredients || p.components || p.ingredient_ids;
    if (Array.isArray(comp) && comp.length > 1) return false;
    return true;
  };

  for (const ing of ingredients || []) {
    const ingName = ing.item || ing.name || "";
    // Resolution chain: explicit id → alias-map (whole-word substring,
    // tier-aware) → fuzzy (best-score). Alias before fuzzy because the
    // alias map handles "egg yolks" / "egg whites" / "olive oil extra
    // virgin" deterministically via authored aliases — fuzzy's score
    // threshold (60) was rejecting these as false-negative even though
    // the registry had a perfectly good answer.
    const ingCanonId = ing.ingredientId
      || inferCanonicalFromName(ingName)
      || resolveNameToCanonicalId(ingName)
      || null;
    const ingCanon = ingCanonId ? findIngredient(ingCanonId) : null;
    const ingState = ing.state || null;

    // Tier 0 — explicit pantryItemId from an upstream swap/bind.
    // Authoritative; honoring it keeps the user's chosen row front
    // and center across cook-prep, preview, and schedule renders.
    let paired = null;
    if (ing.pantryItemId) {
      paired = (pantry || []).find(p =>
        p && !used.has(p.id) && p.id === ing.pantryItemId,
      ) || null;
    }

    // Tier 1a — exact canonical AND state fit. State-awareness here
    // means a recipe asking for "ground chicken" doesn't auto-pair
    // to a whole-bird pantry row; the wrong-state row falls through
    // to be flagged separately so the cook can convert intentionally.
    // resolvesToSameCanonical handles alias redirect (chicken_breast
    // alias → chicken matches a base row tagged chicken + cut=breast).
    let stateMatched = false;
    if (!paired && ingCanonId) {
      paired = (pantry || []).find(p => {
        if (!rowIsRawIngredient(p)) return false;
        const pCanonId = p.ingredientId || p.canonicalId || null;
        if (!pCanonId) return false;
        if (!resolvesToSameCanonical(pCanonId, ingCanonId)) return false;
        if (ingState) {
          const pState = p.state || null;
          if (pState !== ingState) return false;
        }
        return true;
      }) || null;
      if (paired) stateMatched = true;
    }

    // Collect WRONG-STATE candidates: same canonical, present, but in
    // a different physical form than the recipe specified. Only
    // meaningful when the recipe declared a state AND we didn't
    // already land an exact-state pair. These drive the WRONG FORM
    // badge + the convert shortcut ("Have loaf — convert to crumbs").
    let wrongStateCandidates = [];
    if (ingCanonId && ingState && !stateMatched) {
      wrongStateCandidates = (pantry || []).filter(p => {
        if (!p || (p.kind || "ingredient") !== "ingredient") return false;
        if (Number(p.amount) <= 0) return false;
        const pCanonId = p.ingredientId || p.canonicalId || null;
        if (!pCanonId) return false;
        if (!resolvesToSameCanonical(pCanonId, ingCanonId)) return false;
        return (p.state || null) !== ingState;
      });
    }

    // Tier 2 — name-fuzzy match, scoped to genuinely-ambiguous rows.
    // Fires only when AT LEAST ONE side lacks a resolvable canonical
    // — pre-canonical free-text pantry rows, or recipe ingredients
    // the AI left untagged. When BOTH sides carry valid canonicals
    // and Tier 1 didn't match, the canonicals are authoritative.
    if (!paired && ingName) {
      const ingCanonValid = !!(ingCanonId && ingCanon);
      paired = (pantry || []).find(p => {
        if (!rowIsRawIngredient(p)) return false;
        const pCanonId = p.ingredientId || p.canonicalId || null;
        const pCanon   = pCanonId ? findIngredient(pCanonId) : null;
        const pCanonValid = !!(pCanonId && pCanon);
        if (ingCanonValid && pCanonValid) return false;
        return namesMatch(p.name, ingName);
      }) || null;
    }

    // Tier 2.5 — curated substitution. Authored on
    // INGREDIENT_INFO[id].substitutions (parmesan→pecorino,
    // butter→ghee, etc.). Tier-aware: "direct" beats "pro" beats
    // "emergency" beats "dietary". This sits ABOVE generic hub /
    // category fallback so a stocked pecorino lights up before a
    // random feta gets selected as "closest in cheese category."
    let closestMatch = null;
    let subTier = null;
    let subNote = null;
    if (!paired && ingCanonId) {
      const curated = findCuratedSub(ingCanonId);
      if (curated) {
        closestMatch = curated.row;
        subTier      = curated.tier;
        subNote      = curated.note;
      }
    }

    // Tier 3 (REMOVED 2026-05-03): generic closest-match fallback.
    // Used to suggest same-hub siblings (potato ↔ carrot, yellow_onion ↔
    // green_onion, kosher_salt ↔ sea_salt) as "closest match" when
    // the recipe asked for X and the pantry only had Y. Recipe-
    // corrupting: the user typed Boerenkool stamppot expecting
    // POTATOES, not a "we found carrots, close enough" suggestion.
    //
    // Curated substitutions (Tier 2 above) survive because those are
    // AUTHORED relationships in INGREDIENT_INFO.substitutions —
    // explicit, intent-bearing swaps the canonical's curator chose
    // ("greek yogurt → sour cream, direct" / "cilantro → parsley,
    // caution"). Hub-family fuzzy was uncurated and over-matched.
    //
    // After this removal: no curated sub + no exact pantry match
    // → status="missing" → user sees "SHOPPING" (correct). They
    // can still manually SWAP via the picker if they want a creative
    // sub, but nothing's auto-suggested cross-canonical.

    const chosen = paired || closestMatch;
    // Intentionally NOT adding `chosen` to `used` here. The overview's
    // job is "do I have a canonical source for this?" — which is true
    // even when two recipe rows point to the same pantry jar (sea
    // salt, sugar, olive oil, etc.). Marking the row as consumed made
    // the second occurrence render as MISSING despite the jar being
    // right there. Quantity sufficiency is a cook-time concern handled
    // by buildRemovalPlan's FIFO sibling cascade, not by claim-once
    // here. `used` is still maintained for any caller that wants to
    // reason about cross-row claims, but the default pairing pass no
    // longer prevents same-row reuse.

    // Diet-loss diff. Required claims come from the recipe row's
    // stored dietaryClaims (persisted intent) OR re-extracted from
    // the display name as a fallback for older recipes.
    const requiredClaims = Array.isArray(ing.dietaryClaims) && ing.dietaryClaims.length > 0
      ? ing.dietaryClaims
      : extractDietaryClaims(ingName).claims;
    const lostClaims = lostClaimsFor(requiredClaims, chosen);

    // Status priority: same-canonical wins identity, even when the
    // state is wrong. "Have chicken breast — convert to ground" beats
    // "use turkey as a sub" because the recipe author asked for
    // chicken specifically; same-canonical is closer than any swap.
    // The user can still tap SWAP to pick the curated sub if they
    // prefer to skip the conversion.
    let status;
    if (paired) status = "paired";
    else if (wrongStateCandidates.length > 0) status = "wrong-state";
    else if (closestMatch) status = "substitute";
    else status = "missing";

    out.push({
      ingredient: ing,
      resolvedCanonicalId: ingCanonId,
      paired,
      closestMatch,
      subTier,
      subNote,
      wrongStateCandidates,
      lostClaims,
      status,
    });
  }
  return out;
}

// Render-ready description for a single pairing row. Returns
//   { tone, text, subTier, subNote, lostClaims }
// or null when the ingredient shouldn't show a pairing banner
// (e.g. decorative rows with no ingredientId and no name signal).
//
// tone colors (match AIRecipe's existing pill palette):
//   gray   — clean pair, in-kitchen confirmation
//   tan    — branded same-canonical; "we'll use your X"
//   sky    — wrong state (have it, but in a different physical form)
//   amber  — substitute or missing, no dietary conflict
//   red    — dietary conflict (keto/vegan/etc. lost on this sub)
export function describePairing(pairing) {
  if (!pairing) return null;
  const {
    ingredient, paired, closestMatch, lostClaims, status,
    subTier, subNote, wrongStateCandidates,
  } = pairing;
  if (!ingredient?.ingredientId && !ingredient?.item && !ingredient?.name) return null;

  if (status === "paired") {
    const loc = paired.location ? ` from your ${paired.location}` : "";
    const header = deriveRowHeader(paired) || paired.name || "kitchen item";
    return {
      tone: lostClaims.length > 0 ? "red" : "gray",
      text: `We'll use your ${header}${loc}`,
      subTier: null, subNote: null,
      lostClaims,
    };
  }
  if (status === "wrong-state") {
    const candidate = wrongStateCandidates?.[0];
    const stateLabel = candidate?.state || "another form";
    const header = candidate ? (deriveRowHeader(candidate) || candidate.name || "kitchen item") : "it";
    return {
      tone: "sky",
      text: `Have ${header} as ${stateLabel} — convert in Pantry to make ${ingredient.state}`,
      subTier: null, subNote: null,
      lostClaims,
    };
  }
  if (status === "substitute") {
    const header = deriveRowHeader(closestMatch) || closestMatch.name || "kitchen item";
    // Authored substitution: lead with the tier label so the cook
    // sees "Direct sub: Pecorino" instead of a vague "closest match."
    if (subTier) {
      const label = SUB_TIER_LABEL[subTier] || "Substitute";
      return {
        tone: lostClaims.length > 0 ? "red" : "amber",
        text: `${label}: ${header}`,
        subTier,
        subNote,
        lostClaims,
      };
    }
    return {
      tone: lostClaims.length > 0 ? "red" : "amber",
      text: `Closest match in pantry: ${header}`,
      subTier: null,
      subNote: null,
      lostClaims,
    };
  }
  return {
    tone: "amber",
    text: "Not in pantry — add to shopping list",
    subTier: null,
    subNote: null,
    lostClaims: [],
  };
}

// Roll-up summary of "can the user cook this recipe right now?" —
// runs pairRecipeIngredients across the full ingredients[] and
// counts each tier (paired / wrong-state / substitute / missing).
// Used by recipe-browsing surfaces (RecipeBook cards, Home shelf,
// etc.) to render a "READY" badge or "N missing" hint without each
// caller re-implementing the math.
//
// Returns:
//   {
//     ready: bool             // true when nothing is "missing"
//     total: int              // number of recipe ingredients
//     pairedCount: int        // exact pantry match
//     substituteCount: int    // different canonical, has authored sub
//     wrongStateCount: int    // same canonical, wrong physical state
//     missingCount: int       // nothing in pantry resolves
//     missing: Pairing[]      // the raw missing pairings (for display)
//     pairings: Pairing[]     // the full pairings list (raw)
//   }
//
// "ready" intentionally counts substitutes and wrong-state as
// "have something." The user can convert chicken → ground chicken
// at cook time, or sub pecorino for parmesan. The missing tier is
// the only one where the cook would need to leave the kitchen.
//
// Empty recipe (no ingredients[]) returns ready: false — it's not
// "ready to cook" if there's nothing to cook with.
export function computeRecipeMatch(recipe, pantry, options = {}) {
  const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  if (ingredients.length === 0) {
    return {
      ready: false, total: 0,
      pairedCount: 0, substituteCount: 0, wrongStateCount: 0, missingCount: 0,
      missing: [], pairings: [],
    };
  }
  const pairings = pairRecipeIngredients(ingredients, pantry || [], options);
  let paired = 0, sub = 0, wrong = 0, miss = 0;
  const missing = [];
  for (const p of pairings) {
    if (p.status === "paired") paired += 1;
    else if (p.status === "substitute") sub += 1;
    else if (p.status === "wrong-state") wrong += 1;
    else {
      miss += 1;
      missing.push(p);
    }
  }
  return {
    ready: miss === 0,
    total: pairings.length,
    pairedCount: paired,
    substituteCount: sub,
    wrongStateCount: wrong,
    missingCount: miss,
    missing,
    pairings,
  };
}

// Intentionally unused by the consumer API but exported for tests /
// adjacent modules that want the raw claim-set view.
export { pantryRowClaimSet };

// Keep ESLint from flagging INGREDIENTS as unused — imported for
// future extensions (hub scans) and to guarantee the registry is
// warm before consumers call resolve/pair.
export const _REGISTRY = INGREDIENTS;
